import {
  CardCurrenyType,
  CardType,
  OrderType,
  OrderDeliveryType,
  PasswordType,
} from '../constants/fieldValues.constant'
import moment from 'moment'
import * as yup from 'yup'

export const DataTypeSchema = () =>
  yup.object().shape({
    customerInfo: yup
      .string()
      .trim()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(3, 'orders.salaryCardOrder.restrictionTexts.minLengthThree')
      .max(255, 'orders.salaryCardOrder.restrictionTexts.maxLength255')
      .matches(
        /^[a-vx-zA-VX-ZçöüğşıəÇİÖÜĞŞIƏ\s]+$/,
        'orders.salaryCardOrder.restrictionTexts.checkfullName'
      ),
    serialType: yup
      .mixed()
      .oneOf(
        Object.values(PasswordType),
        'orders.salaryCardOrder.restrictionTexts.serialType'
      ),
    serialNo: yup
      .mixed()
      .test('is-valid-serial', function (value) {
        const { serialType } = this.parent
        const checkLength =
          [PasswordType.AA, PasswordType.AB].includes(serialType)
            ? `${value}`.length === 7
            : `${value}`.length === 8
        return (
          checkLength ||
          this.createError({
            path: this.path,
            message:
              [PasswordType.AA, PasswordType.AB].includes(serialType) 
                ? 'orders.salaryCardOrder.restrictionTexts.isValidSerial7'
                : 'orders.salaryCardOrder.restrictionTexts.isValidSerial8',
          })
        )
      })
      .test(
        'is-valid-number',
        'orders.salaryCardOrder.restrictionTexts.isValidNumber',
        function (val) {
          const check = !isNaN(Number(val)) && val != 0
          return check
        }
      ),
    pin: yup
      .string()
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/,
        'orders.salaryCardOrder.restrictionTexts.alphanumeric'
      )
      .test(
        'unique-pin',
        'orders.salaryCardOrder.restrictionTexts.uniquePin',
        function (value) {
          const { tableList } = this?.from[1]?.value || {}
          if (!tableList || !value) return true
          const duplicateCount = tableList.filter(
            (item) => item?.pin?.toLowerCase() === value?.toLowerCase()
          ).length
          return duplicateCount <= 1
        }
      )
      .length(7, 'orders.salaryCardOrder.restrictionTexts.pinLength'),
    position: yup
      .string()
      .trim()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
      .min(2, 'orders.salaryCardOrder.restrictionTexts.minLengthTwo')
      .max(200, 'orders.salaryCardOrder.restrictionTexts.maxLength200')
      .matches(
        /^[a-vx-zA-VX-ZçöüğşıəÇİÖÜĞŞIƏ\s]+$/,
        'orders.salaryCardOrder.restrictionTexts.checkfullName'
      ),
    salary: yup
      .mixed()
      .required()
      .transform((val) => String(val).replace(/\s+/g, ''))
      .test(
        'is-valid-number',
        'orders.salaryCardOrder.restrictionTexts.isValidNumber',
        function (val) {
          const check = !isNaN(Number(val)) && val != 0
          return check
        }
      ),
    phoneNumber: yup
      .mixed()
      .test(
        'is-digits',
        'orders.salaryCardOrder.restrictionTexts.phoneNumber',
        (val) => {
          return /^\d+$/.test(val)
        }
      )
      .test(
        'is-exact-length',
        'orders.salaryCardOrder.restrictionTexts.isExactLength',
        (val) => /^\d{9}$/.test(val)
      )
      .test(
        'is-valid-prefix',
        'orders.salaryCardOrder.restrictionTexts.mobilePhoneCheck',
        (val) => {
          if (!val) return false
          const prefix = String(val).slice(0, 2)
          return [
            '50',
            '51',
            '55',
            '70',
            '77',
            '90',
            '99',
            '10',
            '11',
          ].includes(prefix)
        }
      ),
    // code: yup
    //   .string()
    //   .matches(
    //     /^[A-Za-z0-9]+$/,
    //     'orders.salaryCardOrder.restrictionTexts.alphanumeric'
    //   )
    //   .min(2, 'orders.salaryCardOrder.restrictionTexts.codeWordMin')
    //   .max(30, 'orders.salaryCardOrder.restrictionTexts.codeWordMax')
    //   .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
    email: yup
      .string()
      .email('orders.salaryCardOrder.restrictionTexts.email')
      .test(
        'unique-email',
        'orders.salaryCardOrder.restrictionTexts.uniqueEmail',
        function (value) {
          const { tableList } = this?.from[1]?.value || {}
          if (!tableList || !value) return true
          const duplicateCount = tableList.filter(
            (item) => item?.email?.toLowerCase() === value?.toLowerCase()
          ).length
          return duplicateCount <= 1
        }
      )
      .nullable(),
    // pdfTransactionId: yup.string().nullable(),
  })

export const SalaryCardFormSchema = () =>
  yup.object().shape({
    cardCurrency: yup
      .mixed()
      .oneOf(
        Object.values(CardCurrenyType),
        'orders.salaryCardOrder.restrictionTexts.selectedType'
      ),
    cardType: yup
      .mixed()
      .oneOf(
        Object.values(CardType),
        'orders.salaryCardOrder.restrictionTexts.selectedType'
      ),
    orderType: yup
      .mixed()
      .oneOf(
        Object.values(OrderType),
        'orders.salaryCardOrder.restrictionTexts.selectedType'
      ),
    orderDeliveryType: yup
      .mixed()
      .oneOf(
        Object.values(OrderDeliveryType),
        'orders.salaryCardOrder.restrictionTexts.selectedType'
      ),
    orderBranch: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
    branchTransferred: yup
      .string()
      .required('orders.salaryCardOrder.restrictionTexts.requiredFields'),
    note: yup
      .string()
      .max(50, 'orders.salaryCardOrder.restrictionTexts.checkNote')
      .nullable(),
    trustedPerson: yup.object().shape({
      id: yup.number().nullable(),
      expiryDate: yup
        .string()
        .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
        .test(
          'is-valid-date-range',
          'orders.salaryCardOrder.restrictionTexts.invalidDateRange',
          function (value) {
            if (!value) return false

            const enteredDate = moment(value, 'DD-MM-YYYY')
            if (!enteredDate.isValid()) return false

            const now = moment()
            const minDate = now.clone().add(1, 'month')
            const maxDate = now.clone().add(5, 'years')

            return enteredDate.isBetween(minDate, maxDate, undefined, '[]')
          }
        ),
      fullName: yup
        .string()
        .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
        .min(1, 'orders.salaryCardOrder.restrictionTexts.requiredFields')
        .matches(
          /^[a-vx-zA-VX-ZçöüğşiıəÇÖİÜĞŞIƏwW.\s]+$/,
          'orders.salaryCardOrder.restrictionTexts.checkfullName'
        ),
      pin: yup
        .string()
        .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
        .min(3, 'orders.salaryCardOrder.restrictionTexts.minLengthPin')
        .matches(
          /^[A-Za-z0-9]+$/,
          'orders.salaryCardOrder.restrictionTexts.pinCheck'
        ),
      phone: yup
        .string()
        .required('orders.salaryCardOrder.restrictionTexts.requiredFields')
        .min(1, 'orders.salaryCardOrder.restrictionTexts.requiredFields')
        .matches(
          /^\(\d{2}\) \d{3}-\d{2}-\d{2}$/,
          'orders.salaryCardOrder.restrictionTexts.mobilePhoneCheck'
        ),
    }),
    tableList: yup
      .array()
      .of(DataTypeSchema())
      .min(1, 'orders.salaryCardOrder.restrictionTexts.tableList'),
    // trustedPersonValid: yup.bool().oneOf([false], 'Əlaqədar şəxs məlumatlarını yeniləyin')
  })
